import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import { HiXMark } from "react-icons/hi2";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import type {
  GetStoreListQuery,
  SendPushToSellersByAdminMutation,
  SendPushToSellersByAdminMutationVariables,
} from "../../../__generated__/graphql";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import {
  AdminStoreTableWithMobileCards,
  type AdminStoreListItem,
} from "../components/admin-store-table-with-mobile-cards";
import { AdminPushMessageForm } from "../components/admin-push-message-form";

const SEND_PUSH_TO_SELLER = gql(`
  mutation sendPushToSellersByAdmin($input: Json!) {
    sendPushToSellersByAdmin(input: $input) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  GetStoreListQuery["getStoreList"]
>["result"][number];

function toStoreListItem(store: StoreRow): AdminStoreListItem {
  return {
    id: store.id,
    title: store.title,
    img: store.img,
    address: store.address,
  };
}

const AdminSellerPushAlarm = () => {
  const [keyword, setKeyword] = useState("");
  const [pushTitle, setPushTitle] = useState("");
  const [pushMessage, setPushMessage] = useState("");
  const [selectedStores, setSelectedStores] = useState<StoreRow[]>([]);

  const { data, loading, error } = useQuery<GetStoreListQuery>(GET_STORE_LIST);
  const [sendPushNotification, { loading: sending }] = useMutation<
    SendPushToSellersByAdminMutation,
    SendPushToSellersByAdminMutationVariables
  >(SEND_PUSH_TO_SELLER, {
    onCompleted: (res) => {
      if (res.sendPushToSellersByAdmin.ok) {
        notify("푸시 알림이 발송되었습니다.");
        setPushTitle("");
        setPushMessage("");
        setSelectedStores([]);
      } else {
        notifyError(res.sendPushToSellersByAdmin.error ?? "발송에 실패했습니다.");
      }
    },
  });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keywordLower = keyword.trim().toLowerCase();
  const stores = keywordLower
    ? fullList.filter(
        (s) =>
          s.title.toLowerCase().includes(keywordLower) ||
          (s.address?.toLowerCase().includes(keywordLower) ?? false)
      )
    : fullList;

  const selectedIds = selectedStores.map((s) => s.id);

  const handleStoreSelect = (store: AdminStoreListItem) => {
    const full = fullList.find((s) => s.id === store.id);
    if (!full) return;
    const exists = selectedStores.some((s) => s.id === store.id);
    if (exists) {
      setSelectedStores(selectedStores.filter((s) => s.id !== store.id));
    } else {
      setSelectedStores([...selectedStores, full]);
    }
  };

  const handleSendNotification = () => {
    if (!pushTitle || !pushMessage || selectedStores.length === 0) {
      notifyError("매장, 제목, 메시지를 모두 입력해주세요.");
      return;
    }
    sendPushNotification({
      variables: {
        input: {
          storeIds: selectedIds,
          title: pushTitle,
          message: pushMessage,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="flex justify-center py-8">
          <LoadingSpinnerInline />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }
  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="사장님 푸시 알림"
      description="매장을 선택하고 푸시 알림 제목·본문을 입력한 뒤 발송하세요."
      leftRatio={7}
      rightRatio={3}
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: keyword,
        onSearchChange: setKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={stores.map(toStoreListItem)}
            selectionMode="multiple"
            selectedIds={selectedIds}
            onSelect={handleStoreSelect}
            emptyText={
              keyword.trim() ? "검색 결과가 없습니다." : "매장 목록이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "푸시 발송",
        emptyText: "매장을 선택하고 제목·본문을 입력하세요.",
        children:
          selectedStores.length > 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">선택된 매장:</span>{" "}
                {selectedStores.length}개
              </div>

              <div className="max-h-40 overflow-y-auto space-y-2">
                {selectedStores.map((store) => (
                  <div
                    key={store.id}
                    className="bg-gray-50 rounded-lg p-3 border border-gray-200 relative"
                  >
                    <button
                      type="button"
                      onClick={() => handleStoreSelect(toStoreListItem(store))}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <HiXMark className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-3 pr-8">
                      {store.img ? (
                        <ResponsiveCDNImage
                          src={store.img}
                          alt={store.title}
                          className="w-10 h-10 rounded object-cover flex-shrink-0"
                          sizeRatio={0.1}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {store.title}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {store.address ?? "주소 없음"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <AdminPushMessageForm
                title={pushTitle}
                message={pushMessage}
                onTitleChange={setPushTitle}
                onMessageChange={setPushMessage}
                titleRequired
                messageRequired
                messageRows={6}
              />

              <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                <span className="font-medium">{selectedStores.length}</span>개
                매장의 사장님에게 푸시 알림을 발송합니다.
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setSelectedStores([])}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200 text-sm"
                >
                  선택 해제
                </button>
                <button
                  type="button"
                  onClick={handleSendNotification}
                  disabled={
                    !pushTitle || !pushMessage || selectedStores.length === 0 || sending
                  }
                  className="flex-1 px-4 py-2 bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {sending ? (
                    <>
                      <LoadingSpinnerInline />
                      발송 중...
                    </>
                  ) : (
                    "푸시 알림 발송"
                  )}
                </button>
              </div>
            </div>
          ) : null,
      }}
    />
  );
};

export default AdminSellerPushAlarm;
